import { useState, useEffect, useCallback } from "react"
import { GetAllPacks } from "../api"
import { showNotification } from "aldoo-ra/Notification"

/**
 * Custom hook for managing license packs data
 * Provides centralized pack loading logic for consistency across components
 */
export const usePacksData = () => {
  const [availablePacks, setAvailablePacks] = useState([])
  const [packsLoading, setPacksLoading] = useState(false)
  const [packsError, setPacksError] = useState(null)

  // Load available packs
  const loadAvailablePacks = useCallback(async (showErrors = true) => {
    if (packsLoading) return

    setPacksLoading(true)
    setPacksError(null)
    
    try {
      // Get all active packs
      const packs = await GetAllPacks()
      
      // Handle different response formats
      let packsArray = []
      if (Array.isArray(packs)) {
        packsArray = packs
      } else if (packs && packs.data && Array.isArray(packs.data)) {
        packsArray = packs.data
      } else if (packs && typeof packs === 'object') {
        packsArray = []
      }
      
      setAvailablePacks(packsArray)
      
    } catch (error) {
      setPacksError(error)
      
      if (showErrors) {
        showNotification({
          message: "Failed to load license packs",
          className: "bg-admin_error text-white",
        })
      }
      
      setAvailablePacks([])
    } finally {
      setPacksLoading(false)
    }
  }, []) // Remove packsLoading dependency to prevent infinite loops

  // Auto-load packs on hook initialization (only once)
  useEffect(() => {
    let mounted = true
    
    const loadInitialPacks = async () => {
      if (mounted) {
        await loadAvailablePacks(false) // Don't show errors on initial load
      }
    }
    
    loadInitialPacks()
    
    return () => {
      mounted = false
    }
  }, [loadAvailablePacks])

  // Get pack by ID
  const getPackById = (packId) => {
    if (!packId || !availablePacks.length) return null
    return availablePacks.find(pack => pack._id === packId || pack.id === packId)
  }

  // Format pack for dropdown option
  const formatPackOption = (pack) => {
    if (!pack) return null
    const totalLicenses = pack.statistics?.licenses || pack.totalLicenses || 0
    const activeLicenses = pack.statistics?.active || 0
    const availableLicenses = totalLicenses - activeLicenses
    
    return {
      value: pack._id || pack.id,
      label: `${pack.name || 'Unnamed Pack'} - Available: ${availableLicenses} / ${totalLicenses}`
    }
  }

  // Get dropdown options
  const getPackOptions = () => {
    if (!availablePacks || availablePacks.length === 0) {
      return [{
        value: "",
        label: packsLoading ? "Loading packs..." : "No active license packs available",
        disabled: true
      }]
    }
    
    return availablePacks.map(formatPackOption).filter(Boolean)
  }

  // Get selected pack value for dropdown
  const getSelectedPackValue = (packId) => {
    if (!packId) return []
    
    // If packs are still loading, return a placeholder with the packId
    if (packsLoading || !availablePacks.length) {
      return [{
        value: packId,
        label: "Loading selected pack..."
      }]
    }
    
    const pack = getPackById(packId)
    
    if (!pack) {
      // If pack not found but we have a packId, show a placeholder
      return [{
        value: packId,
        label: `Pack not found (ID: ${packId})`
      }]
    }
    
    return [formatPackOption(pack)]
  }

  return {
    // Data
    availablePacks,
    packsLoading,
    packsError,
    
    // Methods
    loadAvailablePacks,
    getPackById,
    formatPackOption,
    getPackOptions,
    getSelectedPackValue,
    
    // Computed values
    hasActivePacks: availablePacks.length > 0,
    packsCount: availablePacks.length
  }
}

export default usePacksData